import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize, forkJoin } from 'rxjs';
import { ToastService } from '../../../../core/services/toast.service';
import {
  BreadcrumbItem,
  BreadcrumbsComponent,
} from '../../../../shared/components/breadcrumbs/breadcrumbs.component';
import { ServiceApiModel } from '../../../services/models/service.models';
import { ServicesService } from '../../../services/services/services.service';
import { PartnerOrganization } from '../../models/partner.models';
import { PartnersService } from '../../services/partners.service';

@Component({
  selector: 'app-partners',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, BreadcrumbsComponent],
  templateUrl: './partners.component.html',
  styleUrl: './partners.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PartnersComponent {
  private readonly fb = inject(FormBuilder);
  private readonly partnersService = inject(PartnersService);
  private readonly servicesService = inject(ServicesService);
  private readonly toastService = inject(ToastService);

  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly partners = signal<PartnerOrganization[]>([]);
  readonly services = signal<ServiceApiModel[]>([]);
  readonly selectedServiceIds = signal<number[]>([]);
  readonly editingPartnerId = signal<number | null>(null);
  readonly partnerNameUnlocked = signal(false);
  readonly partnerNameWarningOpen = signal(false);
  readonly partnerSearchQuery = signal('');
  readonly partnerServiceFilterIds = signal<number[]>([]);

  readonly breadcrumbItems: BreadcrumbItem[] = [
    { label: 'Home', route: '/home' },
    { label: 'Partners' },
  ];

  readonly partnerForm = this.fb.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(150)]],
  });

  readonly isEditing = computed(() => this.editingPartnerId() !== null);
  readonly isPartnerNameLocked = computed(() => this.isEditing() && !this.partnerNameUnlocked());
  readonly filteredPartners = computed(() => {
    const searchQuery = this.partnerSearchQuery().trim().toLowerCase();
    const serviceFilterIds = this.partnerServiceFilterIds();

    return this.partners().filter((partner) => {
      const matchesSearch = !searchQuery || partner.name.toLowerCase().includes(searchQuery);
      const matchesService = !serviceFilterIds.length
        || serviceFilterIds.some((serviceId) => partner.approvedServiceIds.includes(serviceId));
      return matchesSearch && matchesService;
    });
  });
  readonly serviceNameById = computed(() => {
    const map = new Map<number, string>();
    for (const service of this.services()) {
      map.set(service.id, service.name);
    }
    return map;
  });

  constructor() {
    this.loadPageData();
  }

  startCreate(): void {
    this.editingPartnerId.set(null);
    this.partnerNameUnlocked.set(true);
    this.partnerNameWarningOpen.set(false);
    this.selectedServiceIds.set([]);
    this.partnerForm.reset({ name: '' });
  }

  startEdit(partner: PartnerOrganization): void {
    this.editingPartnerId.set(partner.id);
    this.partnerNameUnlocked.set(false);
    this.partnerNameWarningOpen.set(false);
    this.selectedServiceIds.set([...partner.approvedServiceIds].sort((a, b) => a - b));
    this.partnerForm.reset({ name: partner.name });
  }

  requestPartnerNameUnlock(): void {
    if (!this.isPartnerNameLocked() || this.saving()) {
      return;
    }

    this.partnerNameWarningOpen.set(true);
  }

  confirmPartnerNameUnlock(): void {
    this.partnerNameWarningOpen.set(false);
    this.partnerNameUnlocked.set(true);

    window.setTimeout(() => {
      document.getElementById('partner-name')?.focus();
    });
  }

  cancelPartnerNameUnlock(): void {
    this.partnerNameWarningOpen.set(false);
  }

  toggleServiceSelection(serviceId: number): void {
    if (this.saving()) {
      return;
    }

    const selected = this.selectedServiceIds();
    if (selected.includes(serviceId)) {
      this.selectedServiceIds.set(selected.filter((id) => id !== serviceId));
      return;
    }

    this.selectedServiceIds.set([...selected, serviceId].sort((a, b) => a - b));
  }

  isServiceSelected(serviceId: number): boolean {
    return this.selectedServiceIds().includes(serviceId);
  }

  approvedServiceLabels(partner: PartnerOrganization): string[] {
    const serviceNameById = this.serviceNameById();
    return partner.approvedServiceIds.map(
      (serviceId) => serviceNameById.get(serviceId) ?? `Service #${serviceId}`,
    );
  }

  submit(): void {
    if (this.partnerForm.invalid || this.saving()) {
      this.partnerForm.markAllAsTouched();
      return;
    }

    const raw = this.partnerForm.getRawValue();
    const payload = {
      name: raw.name.trim(),
      approvedServiceIds: [...this.selectedServiceIds()].sort((a, b) => a - b),
    };

    if (!payload.name) {
      this.toastService.warning('Invalid Organization Name', 'Organization name is required.');
      return;
    }

    const editingId = this.editingPartnerId();
    this.saving.set(true);

    const request$ = editingId
      ? this.partnersService.updatePartner(editingId, payload)
      : this.partnersService.createPartner(payload);

    request$.pipe(finalize(() => this.saving.set(false))).subscribe({
      next: () => {
        this.toastService.success(
          editingId ? 'Partner Updated' : 'Partner Created',
          editingId
            ? 'Partner organization details were updated.'
            : 'Partner organization created successfully.',
        );
        this.startCreate();
        this.loadPartners();
      },
      error: (error) => {
        this.toastService.danger(
          editingId ? 'Update Failed' : 'Create Failed',
          error?.error?.message ?? 'Unable to save partner organization right now.',
        );
      },
    });
  }

  trackById(_index: number, item: { id: number }): number {
    return item.id;
  }

  setPartnerSearchQuery(value: string): void {
    this.partnerSearchQuery.set(value);
  }

  togglePartnerServiceFilter(serviceId: number): void {
    const selected = this.partnerServiceFilterIds();
    if (selected.includes(serviceId)) {
      this.partnerServiceFilterIds.set(selected.filter((id) => id !== serviceId));
      return;
    }

    this.partnerServiceFilterIds.set([...selected, serviceId].sort((a, b) => a - b));
  }

  isPartnerServiceFilterSelected(serviceId: number): boolean {
    return this.partnerServiceFilterIds().includes(serviceId);
  }

  resetPartnerFilters(): void {
    this.partnerSearchQuery.set('');
    this.partnerServiceFilterIds.set([]);
  }

  private loadPageData(): void {
    this.loading.set(true);
    this.errorMessage.set(null);

    forkJoin({
      partners: this.partnersService.listPartners(),
      services: this.servicesService.listServices(true),
    })
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: ({ partners, services }) => {
          this.partners.set(partners.data ?? []);
          this.services.set(services.data ?? []);
          this.startCreate();
        },
        error: (error) => {
          this.partners.set([]);
          this.services.set([]);
          this.errorMessage.set(
            error?.error?.message ?? 'Unable to load partner organizations right now.',
          );
        },
      });
  }

  private loadPartners(): void {
    this.loading.set(true);
    this.errorMessage.set(null);

    this.partnersService
      .listPartners()
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: ({ data }) => {
          this.partners.set(data ?? []);
        },
        error: (error) => {
          this.partners.set([]);
          this.errorMessage.set(
            error?.error?.message ?? 'Unable to refresh partner organizations right now.',
          );
        },
      });
  }
}
